<template>
  <section id="services" class="text-white py-13 min-h-screen">
    <div class="container mx-auto text-center">
      <div class="section-title" data-aos="fade-down">
        <h1 class="text-4xl lg:text-7xl font-bold text-[#e4ac3b] uppercase drop-shadow-[2px_2px_5px_rgba(228,172,59,0.7)]">
          Our Services
        </h1>
        <div class="w-80 h-px border border-[#e4ac3b] mx-auto mt-2 drop-shadow-[2px_2px_5px_rgba(228,172,59,0.7)]"></div>
      </div>

      <!-- IT Services Group -->
      <div class="services-group-wrapper" data-aos="fade-up">
        <div class="services-group-header">
          <h2>IT Services</h2>
        </div>
        <div class="services-grid">
          <div
            class="service-card"
            v-for="service in itServices"
            :key="service.id"
            @click="openModal(service.modalId)"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div class="service-content">
              <div class="service-image">
                <img :src="service.image" :alt="service.title" />
              </div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Marketing Services Group -->
      <div class="services-group-wrapper" data-aos="fade-up">
        <div class="services-group-header">
          <h2>Marketing Services</h2>
        </div>
        <div class="services-grid">
          <div
            class="service-card"
            v-for="service in marketingServices"
            :key="service.id"
            @click="openModal(service.modalId)"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <div class="service-content">
              <div class="service-image">
                <img :src="service.image" :alt="service.title" />
              </div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Modal -->
    <div 
      v-if="selectedService"
      class="modal-overlay"
      @click="closeModal"
    >
      <div class="modal-content" @click.stop>
        <button @click="closeModal" class="modal-close">✕</button>
        <div class="modal-grid">
          <div class="modal-image-wrapper">
            <img 
              class="modal-image"
              :src="selectedService.image"
              :alt="selectedService.title"
            />
          </div>
          <div class="modal-info">
            <h2>{{ selectedService.title }}</h2>
            <p class="modal-description">
              {{ selectedService.fullDescription }}
            </p>
            <div class="modal-features">
              <h3>Key Features:</h3>
              <ul>
                <li v-for="(feature, index) in selectedService.features" :key="index">
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useAos } from '../hooks/useAos'

let id = 0;

const services = ref([
  // IT Services
  {
    id: id++,
    group: 'IT',
    modalId: 'webapp',
    image: new URL('../assets/images/services/webapp.svg', import.meta.url).href,
    title: 'Web & App Development',
    description: 'From Concept to Code: Building Your Digital Identity.',
    fullDescription: 'Our web and app development services transform your ideas into powerful digital experiences. We create responsive, user-friendly applications that drive engagement and deliver results.',
    features: [
      'Custom website development',
      'E-commerce solutions',
      'Progressive Web Apps (PWAs)',
      'Cross-platform compatibility',
      'Performance optimization'
    ]
  },
  {
    id: id++,
    group: 'IT',
    modalId: 'mobile',
    image: new URL('../assets/images/services/mobiledev.svg', import.meta.url).href,
    title: 'Mobile Development',
    description: 'Navigating the Digital Landscape with Expert IT Solutions and Mobile Development.',
    fullDescription: 'We build native and cross-platform mobile applications that provide seamless user experiences across all devices. Our mobile solutions are designed to meet the unique needs of your business.',
    features: [
      'iOS and Android development',
      'React Native applications',
      'Flutter development',
      'Mobile UI/UX design',
      'App store optimization'
    ]
  },
  {
    id: id++,
    group: 'IT',
    modalId: 'software',
    image: new URL('../assets/images/services/softwaredev.svg', import.meta.url).href,
    title: 'Software Development',
    description: 'Elevate Your Operations with Custom Software Solutions.',
    fullDescription: 'Our custom software solutions streamline operations, enhance productivity, and drive innovation. We develop scalable, secure, and efficient software tailored to your specific business requirements.',
    features: [
      'Custom enterprise software',
      'API development and integration',
      'Database design and optimization',
      'Cloud-based solutions',
      'Legacy system modernization'
    ]
  },
  {
    id: id++,
    group: 'IT',
    modalId: 'automation',
    image: new URL('../assets/images/services/automation.svg', import.meta.url).href,
    title: 'Automation',
    description: 'Streamline Your Workflow with Intelligent Automation Solutions.',
    fullDescription: 'Transform your business processes with intelligent automation solutions. We help you reduce manual tasks, improve efficiency, and minimize errors through smart automation technologies.',
    features: [
      'Workflow automation',
      'Robotic Process Automation (RPA)',
      'Business process optimization',
      'Integration with existing systems',
      'AI-powered automation'
    ]
  },
  // Marketing Services
  {
    id: id++,
    group: 'Marketing',
    modalId: 'digital',
    image: new URL('../assets/images/services/digital.svg', import.meta.url).href,
    title: 'Digital Marketing',
    description: 'Unlocking Growth Potential in the Digital Sphere',
    fullDescription: 'Our digital marketing strategies help you reach the right audience, increase brand visibility, and drive conversions. We use data-driven approaches to optimize your online presence and achieve measurable results.',
    features: [
      'Search Engine Optimization (SEO)',
      'Pay-Per-Click (PPC) advertising',
      'Content marketing strategy',
      'Email marketing campaigns',
      'Analytics and reporting'
    ]
  },
  {
    id: id++,
    group: 'Marketing',
    modalId: 'brand',
    image: new URL('../assets/images/services/socmed.svg', import.meta.url).href,
    title: 'Brand Awareness and Social Media Management',
    description: 'Your Brand\'s Best Advocate in a Noisy Digital World.',
    fullDescription: 'We help you build a strong brand presence across social media platforms. Our team creates engaging content, manages your online communities, and fosters meaningful connections with your audience.',
    features: [
      'Social media strategy development',
      'Content creation and curation',
      'Community management',
      'Influencer partnerships',
      'Brand voice and messaging'
    ]
  }
])

const selectedService = ref(null)

const openModal = (modalId) => {
  const service = services.value.find(s => s.modalId === modalId)
  if (service) {
    selectedService.value = service
    document.body.style.overflow = 'hidden'
  }
}

const closeModal = () => {
  selectedService.value = null
  document.body.style.overflow = ''
}

const itServices = computed(() => services.value.filter(s => s.group === 'IT'))
const marketingServices = computed(() => services.value.filter(s => s.group === 'Marketing'))

useAos()

</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
}

.section-title {
  margin-bottom: 20px;
  border-bottom: none
}

.services-group-wrapper {
  margin-bottom: 80px;
  animation: fadeInUp 0.8s ease-in-out;
}

.services-group-wrapper:last-child {
  margin-bottom: 0;
}

.services-group-header {
  text-align: center;
  margin-bottom: 50px;
}

.services-group-header h2 {
  font-size: 42px;
  font-weight: 700;
  color: #e4ac3b;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(228, 172, 59, 0.5);
}

.services-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
}

.service-card {
  width: calc(33.333% - 20px);
  min-width: 300px;
  max-width: 380px;
  background: #111111;
  border: 1px solid #0b090a;
  border-radius: 12px;
  box-shadow: 7px 7px 14px #0a0a0a, -7px -7px 14px #131313;
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: pointer;
}

.services-group-wrapper:last-child .service-card {
  width: calc(50% - 15px);
}

.service-card:hover {
  transform: scale(1.05);
  border-color: #e4ac3b;
  box-shadow: 0 0 20px rgba(228, 125, 57, 0.8), 0 0 25px rgba(255, 159, 69, 0.7);
}

.service-card:active {
  transform: scale(0.95);
}

.service-content {
  padding: 40px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
}

.service-image {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  transition: all 0.3s ease;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 0 15px rgba(228, 172, 59, 0.3));
  transition: all 0.3s ease;
}

.service-card:hover .service-image img {
  transform: scale(1.1);
  filter: drop-shadow(0 0 20px rgba(228, 172, 59, 0.6));
}

.service-content h3 {
  font-size: 22px;
  font-weight: 600;
  color: #e4ac3b;
  margin-bottom: 15px;
  line-height: 1.4;
  transition: all 0.3s ease;
}

.service-card:hover .service-content h3 {
  color: #FF9F45;
  text-shadow: 0 0 10px rgba(228, 172, 59, 0.6);
}

.service-content p {
  font-size: 16px;
  color: #b0b0b0;
  line-height: 1.6;
  transition: all 0.3s ease;
}

.service-card:hover .service-content p {
  color: #e0e0e0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.3s ease-in-out;
}

.modal-content {
  position: relative;
  background: #111111;
  border: 1px solid #e4ac3b;
  border-radius: 16px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 0 40px rgba(228, 172, 59, 0.5);
  animation: slideUp 0.3s ease-in-out;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: #e4ac3b;
  font-size: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.modal-close:hover {
  color: #FF6B00;
  background: rgba(228, 172, 59, 0.1);
  box-shadow: 0 0 20px rgba(228, 172, 59, 0.5);
}

.modal-close:active {
  transform: scale(0.95);
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  padding: 50px;
}

.modal-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0a0a0a;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #1a1a1a;
}

.modal-image {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: contain;
  filter: drop-shadow(0 0 20px rgba(228, 172, 59, 0.4));
}

.modal-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.modal-info h2 {
  font-size: 32px;
  font-weight: 700;
  color: #e4ac3b;
  line-height: 1.3;
  text-shadow: 0 0 10px rgba(228, 172, 59, 0.5);
}

.modal-description {
  font-size: 18px;
  color: #e0e0e0;
  line-height: 1.7;
}

.modal-features h3 {
  font-size: 22px;
  font-weight: 600;
  color: #e4ac3b;
  margin-bottom: 15px;
}

.modal-features ul {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-features li {
  font-size: 16px;
  color: #b0b0b0;
  padding-left: 25px;
  position: relative;
  line-height: 1.5;
}

.modal-features li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #e4ac3b;
  font-size: 24px;
  line-height: 1;
}



@media(min-width:1024px){
  .section-title {
    font-size: 4rem; /* large screens */
    padding-bottom: 10px;
    border-bottom-width: 5px;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile Responsiveness */
@media (max-width: 1024px) {
  .service-card {
    width: calc(50% - 15px);
  }
  
  .services-group-wrapper:last-child .service-card {
    width: calc(50% - 15px);
  }
  
  .modal-grid {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 40px 30px;
  }
  
  .modal-image-wrapper {
    padding: 20px;
  }
  
  .modal-image {
    max-height: 300px;
  }
}

@media (max-width: 768px) {
  .services-group-wrapper {
    margin-bottom: 60px;
  }

  .services-group-header h2 {
    font-size: 32px;
  }

  .services-grid {
    gap: 20px;
  }

  .service-card,
  .services-group-wrapper:last-child .service-card {
    width: 100%;
    max-width: 100%;
    min-width: unset;
  }

  .service-content {
    padding: 30px 20px;
  }

  .service-image {
    width: 100px;
    height: 100px;
    margin-bottom: 20px;
  }

  .service-content h3 {
    font-size: 20px;
  }

  .service-content p {
    font-size: 14px;
  }
  
  .modal-grid {
    padding: 30px 20px;
    gap: 25px;
  }
  
  .modal-close {
    top: 15px;
    right: 15px;
    font-size: 28px;
  }
  
  .modal-info h2 {
    font-size: 26px;
  }
  
  .modal-description {
    font-size: 16px;
  }
  
  .modal-features h3 {
    font-size: 20px;
  }
  
  .modal-features li {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .services-group-header h2 {
    font-size: 24px;
  }

  .service-content {
    padding: 25px 15px;
  }

  .service-image {
    width: 80px;
    height: 80px;
    margin-bottom: 15px;
  }

  .service-content h3 {
    font-size: 18px;
  }

  .service-content p {
    font-size: 13px;
  }
  
  .modal-grid {
    padding: 25px 15px;
  }
  
  .modal-close {
    top: 10px;
    right: 10px;
    font-size: 24px;
    width: 35px;
    height: 35px;
  }
  
  .modal-info h2 {
    font-size: 22px;
  }
  
  .modal-description {
    font-size: 14px;
  }
  
  .modal-features h3 {
    font-size: 18px;
  }
  
  .modal-features li {
    font-size: 13px;
    padding-left: 20px;
  }
}
</style>